import { apiRequest } from "./api-request";

// Prototyp "tobis": Client fuer die BFF-Ingest-Routen. Handgeschriebene Typen
// statt codegen, weil die Schiene REST ist — das Datenmodell steckt in jsonb und
// aendert sich in der Explorationsphase laufend, ein GraphQL-Schema muesste
// jedes Feld vorab deklarieren.

// Muss mit SOURCES in apps/bff/src/tobis/runner.ts uebereinstimmen: die
// Admin-Ansicht bekommt die Liste zur Laufzeit vom BFF, der Typ hier deckt nur
// ab, was sie anzeigen und ausloesen kann.
export type TobisSource =
  | "imdb"
  | "tvmaze-catalog"
  | "tvmaze"
  | "omdb"
  | "agf"
  | "ffa"
  | "tmdb"
  | "tmdb-us"
  | "tmdb-tobis"
  | "tmdb-person-gaps"
  | "lumiere"
  | "lumiere-search";

export interface TobisStat {
  source: string;
  kind: string;
  current: number;
  superseded: number;
  lastFetchedAt: string | null;
}

export interface TobisRun {
  id: string;
  source: string;
  startedAt: string;
  finishedAt: string | null;
  status: string;
  recordsSeen: number;
  recordsNew: number;
  recordsChanged: number;
  error: string | null;
}

/**
 * Ein getakteter Ingest-Lauf (siehe apps/bff/src/tobis/scheduler.ts). Nur die
 * Quellen mit eigenem Timer tauchen hier auf - die uebrigen laufen weiter
 * ausschliesslich auf Zuruf ueber "Fetch now".
 */
export interface TobisSchedule {
  source: string;
  /** Cron-Ausdruck in Servers lokaler Zeit, z.B. "0 10 * * *". */
  schedule: string;
  enabled: boolean;
  /** ISO-Zeitstempel des naechsten planmaessigen Ticks. */
  nextRun: string;
}

/** Ein Punkt der Verlaufs-Sparkline einer Quellen-Karte, aeltester zuerst. */
export interface TobisRunPoint {
  startedAt: string;
  status: string;
  recordsNew: number;
  recordsSeen: number;
}

export interface TobisOverview {
  stats: TobisStat[];
  sources: TobisSource[];
  runs: TobisRun[];
  schedules: TobisSchedule[];
  /** null oder fehlender Eintrag = zaehlt unter dem eigenen Namen. Siehe
   *  sourceStoresAs im BFF: manche Quellen zahlen bewusst in den Datentopf
   *  einer anderen ein (z.B. tvmaze-catalog -> tvmaze). */
  storesAs: Record<string, string | null>;
  /** Die letzten paar Laeufe je Quelle, aeltester zuerst - siehe runHistoryBySource im BFF. */
  runHistory: Record<string, TobisRunPoint[]>;
}

// Die modellierte Sicht. Zahlen kommen als string, weil Postgres numeric und
// bigint so serialisiert: beides passt nicht verlustfrei in eine JS-Zahl.
export interface TobisTitle {
  id: string;
  source: string;
  medium: "series" | "movie";
  imdbId: string | null;
  name: string | null;
  kind: string | null;
  status: string | null;
  year: number | null;
  premiered: string | null;
  ended: string | null;
  language: string | null;
  runtimeMin: number | null;
  genres: string[];
  network: string | null;
  country: string | null;
  imdbRating: string | null;
  imdbVotes: string | null;
  /** TMDB-Bewertungsdurchschnitt (0-10), aus bulk.justwatch_movie (Markt DE). */
  tmdbScore: string | null;
  metascore: string | null;
  boxOfficeUsd: string | null;
  awardWins: number | null;
  awardsText: string | null;
  enriched: boolean;
  castSize: number;
  /** OMDb-Poster, sonst TMDB. */
  coverUrl: string | null;
  /** TMDB (deutsch) ist Vorrang, OMDb-Plot (englisch) der Rueckfall. */
  summary: string | null;
  popularity: number | null;
  /** Nur aus TMDB. */
  originalTitle: string | null;
  /** TMDB, weltweit, in USD. Absichtlich getrennt von boxOfficeUsd (OMDb, meist US-Kinoumsatz). */
  budgetUsd: string | null;
  revenueUsd: string | null;
  /** Besucherzahl DE, beste verfuegbare Quelle (FFA oder LUMIERE). */
  admissionsDe: string | null;
  /** 'ffa' oder 'lumiere'. null, wenn keine Zahl vorliegt. */
  admissionsSource: string | null;
  /** Fuer den Link auf die TMDB-Seite. null ohne TMDB-Treffer. */
  tmdbId: string | null;
  /** Fuer den Link auf lumiere.obs.coe.int. Nur gesetzt bei admissionsSource = 'lumiere'. */
  lumiereId: string | null;
  /**
   * Letzter Ingest-Kontakt als ISO-Zeitstempel. NUR bei sort='updated' gesetzt -
   * in den anderen Sortierungen wird die Hilfssicht gar nicht gejoint (V0110).
   * Der Wert 1970-01-01 bedeutet "nie ueber eine API angefasst" (reiner
   * Bulk-Import) und wird nicht angezeigt.
   */
  updatedAt?: string;
  /**
   * Nur gesetzt, wenn die Anfrage keywordIds enthielt (Tonalitaets-Rangierung
   * im Labor, siehe fetchTitles). null = kein TMDB-Treffer (Tonalitaet
   * unbekannt) - anders als 0 (TMDB-Treffer, aber keine Ueberlappung). Ein
   * Titel ohne TMDB-Daten fehlt deswegen nicht im Pool, er landet nur nicht
   * oben in der Rangierung.
   */
  keywordMatchCount: number | null;
  /**
   * Nur gesetzt, wenn die Anfrage personIds enthielt (Regie/Drehbuchautor:in/
   * Cast im Labor-Pitch, siehe fetchTitles). Zaehlt model.credit-Treffer
   * gegen die gesuchten Personen, unabhaengig von Rolle (Cast ODER Crew,
   * ein Regie-Credit zaehlt genauso). Anders als keywordMatchCount gibt es
   * hier keine Quelle-ohne-Treffer-Zwischenstufe (model.credit deckt den
   * ganzen Katalog): 0 heisst direkt kein Overlap, kein TMDB-Sonderfall.
   */
  personMatchCount: number | null;
}

/** Eine Auszeichnung einer Person (Wikidata, model.person_award). */
export interface TobisPersonAward {
  award: string;
  won: boolean;
  year: number | null;
  /** IMDb-ID des Films, fuer den die Auszeichnung vergeben wurde. Null, wenn
   *  Wikidata das nicht fuehrt (die Haelfte der Faelle, siehe BFF). */
  workImdbId: string | null;
}

export interface TobisTalent {
  personId: string;
  /**
   * Letzter Ingest-Kontakt als ISO-Zeitstempel. NUR bei sort='updated' gesetzt -
   * in den anderen Sortierungen wird die Hilfssicht gar nicht gejoint (V0111).
   * 1970 bedeutet "nie ueber eine API angefasst", siehe isRealUpdateTimestamp.
   */
  updatedAt?: string | null;
  name: string | null;
  birthday: string | null;
  country: string | null;
  creditCount: number;
  /** 'tvmaze' oder 'tmdb'. Getrennte Identitaetsraeume, eine Person kann zwei Profile haben. */
  source: string;
  /** Nur bei TMDB-Personen gesetzt. */
  photoUrl: string | null;
  /** Gewonnene und nominierte Auszeichnungen (Wikidata). Leer, wenn keine erfasst sind. */
  awards: TobisPersonAward[];
  /**
   * Rohe Quell-IDs (model.talent_unified), Praefix "tvmaze:"/"tmdb:" (siehe
   * model.talent). Gesetzt, wenn die Person ueber model.person_alias mit einem
   * Profil der jeweils anderen Quelle zusammengefuehrt wurde, sonst nur eine
   * der beiden. Fuer den Quellen-Link auf der Personenseite - kein IMDb-Pendant:
   * anders als bei Titeln (imdb_id als Bruecke) gibt es fuer Personen keine
   * verlaessliche IMDb-Zuordnung, nur einen Namensabgleich (siehe dossier.ts),
   * der fuer einen klickbaren Link zu unsicher ist.
   */
  tvmazePersonId: string | null;
  tmdbPersonId: string | null;
  /**
   * 'high' | 'low' bei einer zusammengefuehrten Person (model.person_alias-
   * Merge, V0100), null ohne Merge (kein Alias-Zweig - da hat kein Merge
   * stattgefunden, nichts zu bewerten). 'low' markiert das verifizierte
   * Hochrisiko-Muster der unique_name-Regel (V0050, reiner Namensabgleich):
   * kein Jahresueberlapp der beiden Filmografien UND duenne Beleglage auf
   * beiden Seiten - siehe TobisTalentPage-Hinweis.
   */
  confidence: "high" | "low" | null;
}

export interface TobisCredit {
  tvmazeId: string | null;
  coverUrl: string | null;
  medium: string | null;
  year: number | null;
  titleName: string | null;
  characterName: string | null;
  premiered: string | null;
  ended: string | null;
  imdbId: string | null;
  isSelf: boolean;
  isVoice: boolean;
  roleCount: number;
  /** Crew-Rollen auf diesem Titel, z.B. ["Director", "Screenplay"]. Leer bei reinem Schauspiel. */
  jobs: string[];
  /** Besucherzahl DE, wie TobisTitle.admissionsDe. null ohne FFA/LUMIERE-Erhebung. */
  admissionsDe: string | null;
  admissionsSource: string | null;
  /** Weltweit, USD, TMDB - wie TobisTitle.revenueUsd/budgetUsd. Fuer den KI-Kontext im Bewertungsschritt (find-comparables). */
  revenueUsd: string | null;
  budgetUsd: string | null;
  summary: string | null;
  genres: string[];
}

export class TobisNotConfiguredError extends Error {}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const { response } = await apiRequest(path, { signal });
  if (response.status === 501) throw new TobisNotConfiguredError();
  if (!response.ok) throw new Error(`${path} → ${response.status}`);
  return (await response.json()) as T;
}

export async function fetchOverview(runLimit = 20): Promise<TobisOverview> {
  const [stats, runs] = await Promise.all([
    getJson<{
      stats: TobisStat[];
      sources: TobisSource[];
      schedules: TobisSchedule[];
      storesAs: Record<string, string | null>;
      runHistory: Record<string, TobisRunPoint[]>;
    }>("tobis-api/stats"),
    getJson<{ runs: TobisRun[] }>(`tobis-api/runs?limit=${runLimit}`),
  ]);
  return {
    stats: stats.stats,
    sources: stats.sources,
    schedules: stats.schedules,
    storesAs: stats.storesAs,
    runHistory: stats.runHistory,
    runs: runs.runs,
  };
}

// Filter als flaches Objekt, wie ExtendedPersonFilters in der erweiterten Suche.
// Zahlen liegen als Text vor, damit ein leeres Feld "kein Filter" bedeutet und
// nicht 0.
export interface TobisTitleFilters {
  search: string;
  medium: "" | "movie" | "series";
  genres: readonly string[];
  languages: readonly string[];
  yearFrom: string;
  yearTo: string;
  ratingMin: string;
  votesMin: string;
  /** Werte aus FilterOptions.sources, z.B. "tmdb", "lumiere". ODER-verknuepft. */
  sources: readonly string[];
  admissionsMin: string;
  /** Werte aus FilterOptions.companies (TMDB production_companies). ODER-verknuepft. */
  companies: readonly string[];
  /** Werte aus FilterOptions.distributors (model.dfff_projekt.verleih, nur source='dfff'). ODER-verknuepft. */
  distributors: readonly string[];
  runtimeMin: string;
  /** Werte aus FilterOptions.titleCountries. ODER-verknuepft. */
  countries: readonly string[];
  /** Werte aus FilterOptions.statuses. ODER-verknuepft. */
  statuses: readonly string[];
  /** Werte aus FilterOptions.networks. ODER-verknuepft. */
  networks: readonly string[];
  /** Werte aus FilterOptions.awards. Matcht nur GEWONNENE Auszeichnungen, ODER-verknuepft. */
  awardsWon: readonly string[];
  /** Nur Titel mit zugeordneter Drehbuchfassung (IMSDb/scriptslug, model.title_script). */
  hasScript: boolean;
  /**
   * Nur Titel mit Titelbild. Standardmaessig true (siehe EMPTY_TITLE_FILTERS) -
   * anders als die uebrigen Filter hier eine bewusste Anzeige-Voreinstellung,
   * kein Datenkriterium, das der Nutzer erst waehlen muss.
   */
  hasImage: boolean;
  /**
   * Externe Kennungen (bulk.lumiere_film_identifier). ODER-verknuepft wie
   * sources. Daten-Deckungs-/Qualitaetsfilter, kein fachliches Kriterium -
   * "eidrBroken" macht eine Lieferungsluecke der Quelle sichtbar (LUMIERE/
   * Directus liefert bei ~56% aller EIDR-Werte kein Pruefzeichen).
   */
  identifiers: readonly string[];
}

export interface TobisTalentFilters {
  search: string;
  countries: readonly string[];
  ageFrom: string;
  ageTo: string;
  creditsMin: string;
  /** '' (alle), 'tvmaze' oder 'tmdb'. Getrennte Identitaetsraeume, siehe Talent.source. */
  source: "" | "tvmaze" | "tmdb";
  /** Aus model.credit abgeleitet (kein Feld auf Talent), siehe talentWhere im BFF. */
  roleType: "" | "acting" | "directing" | "producing" | "crew";
  /** Mindestens ein Credit dieses Mediums (model.credit.medium). */
  mediumType: "" | "movie" | "series";
}

// Sortierung liegt bewusst nicht in TobisTalentFilters: "Filter zuruecksetzen"
// soll die Sortierung nicht mit anfassen (gleiche Trennung wie bei SHARE, siehe
// ShareMinePage: filter- und sort-State sind zwei getrennte Zustaende).
/** Sortierungen der Personenliste. "updated" = letzter Ingest-Kontakt (V0111). */
export type TobisTalentSort = "credits" | "az" | "admissions" | "updated";

/**
 * Sortierung des Registers. "release" (Voreinstellung) zeigt das Neueste
 * zuerst, "votes" die Relevanz aus Stimmenzahl und Popularitaet. Jede hat im
 * BFF ihren eigenen Index und ihre eigene Cursor-Form - ein Wechsel setzt die
 * geladene Seitenfolge deshalb zurueck.
 */
/** Sortierungen der Titelliste. "updated" = letzter Ingest-Kontakt (V0110). */
export type TobisTitleSort = "release" | "votes" | "updated";

export const EMPTY_TITLE_FILTERS: TobisTitleFilters = {
  search: "",
  medium: "",
  genres: [],
  languages: [],
  yearFrom: "",
  yearTo: "",
  ratingMin: "",
  votesMin: "",
  sources: [],
  admissionsMin: "",
  companies: [],
  distributors: [],
  runtimeMin: "",
  countries: [],
  statuses: [],
  networks: [],
  awardsWon: [],
  hasScript: false,
  hasImage: true,
  identifiers: [],
};

export const EMPTY_TALENT_FILTERS: TobisTalentFilters = {
  search: "",
  countries: [],
  ageFrom: "",
  ageTo: "",
  creditsMin: "",
  source: "",
  roleType: "",
  mediumType: "",
};

export function titleFiltersActive(f: TobisTitleFilters): boolean {
  return (
    f.search.trim() !== "" ||
    f.medium !== "" ||
    f.genres.length > 0 ||
    f.languages.length > 0 ||
    f.yearFrom !== "" ||
    f.yearTo !== "" ||
    f.ratingMin !== "" ||
    f.votesMin !== "" ||
    f.sources.length > 0 ||
    f.admissionsMin !== "" ||
    f.companies.length > 0 ||
    f.distributors.length > 0 ||
    f.runtimeMin !== "" ||
    f.countries.length > 0 ||
    f.statuses.length > 0 ||
    f.networks.length > 0 ||
    f.awardsWon.length > 0 ||
    f.hasScript ||
    // hasImage ist standardmaessig an (siehe EMPTY_TITLE_FILTERS) - "aktiv"
    // heisst hier: der Nutzer hat die Voreinstellung abgeschaltet, nicht dass
    // sie steht.
    !f.hasImage ||
    f.identifiers.length > 0
  );
}

export function talentFiltersActive(f: TobisTalentFilters): boolean {
  return (
    f.search.trim() !== "" ||
    f.countries.length > 0 ||
    f.ageFrom !== "" ||
    f.ageTo !== "" ||
    f.creditsMin !== "" ||
    f.source !== "" ||
    f.roleType !== "" ||
    f.mediumType !== ""
  );
}

// Leere Werte fallen raus, damit die URL nur zeigt, was wirklich filtert.
function toQuery(entries: Record<string, string | boolean | readonly string[]>, limit: number): string {
  const q = new URLSearchParams({ limit: String(limit) });
  for (const [key, value] of Object.entries(entries)) {
    if (Array.isArray(value)) {
      if (value.length) q.set(key, value.join(","));
    } else if (typeof value === "boolean") {
      if (value) q.set(key, "true");
    } else if (String(value).trim() !== "") {
      q.set(key, String(value).trim());
    }
  }
  return q.toString();
}

export interface SourceOption {
  value: string;
  label: string;
}

export interface FilterOptions {
  genres: string[];
  languages: string[];
  /** Personen-Laender (ISO-Codes). Fuer Titel-Produktionslaender siehe titleCountries. */
  countries: string[];
  sources: SourceOption[];
  /** Produktionsfirmen mit mehreren Titeln im Bestand (Rauschfilter im BFF), nach Haeufigkeit sortiert. */
  companies: string[];
  /** Kino-Verleiher (model.dfff_projekt.verleih), nur bei source='dfff' gesetzt, alphabetisch. */
  distributors: string[];
  /** Titel-Produktionslaender, einzeln aus dem IMDb-Freitextfeld gesplittet. */
  titleCountries: string[];
  /** TV-Sender, nach Haeufigkeit sortiert. */
  networks: string[];
  /** Serienstatus, Rauschfilter siehe statusOptions im BFF. */
  statuses: string[];
  /** Gewonnene Auszeichnungen (Wikidata), Rauschfilter siehe awardOptions im BFF. */
  awards: string[];
  distributions: FilterDistributions;
}

/** Ein Bucket eines Histogramms: `value` ist der Bucket-Anfang, nicht die Mitte. */
export interface DistributionBucket {
  value: number;
  count: number;
}

/** Haeufigkeits-Histogramme je Zahlenfilter, siehe numericDistributions im BFF. */
export interface FilterDistributions {
  /** 5-Jahres-Buckets. */
  year: DistributionBucket[];
  /** 0,5er-Buckets, 0-10. */
  rating: DistributionBucket[];
  /** Zehnerpotenz-Buckets: value 0 = "1-9 Stimmen", 3 = "1.000-9.999" usw. */
  votes: DistributionBucket[];
  /** Zehnerpotenz-Buckets wie votes. */
  admissions: DistributionBucket[];
  /** 15-Minuten-Buckets, bei 240 gedeckelt. */
  runtime: DistributionBucket[];
}

export async function fetchFilterOptions(): Promise<FilterOptions> {
  return getJson<FilterOptions>("tobis-api/filter-options");
}

export interface PageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface TobisTitlePage {
  titles: TobisTitle[];
  pageInfo: PageInfo;
  /** Gesamtzahl passend zu den Filtern, nur auf der ersten Seite gesetzt
   *  (kein `after`) - der Aufrufer haelt den Wert selbst ueber weitere
   *  Seiten hinweg fest, siehe countTitles im BFF. */
  total?: number;
}

/**
 * Eine Seite des Titelkatalogs. `after` ist der endCursor der Vorseite; ohne
 * ihn kommt die erste Seite.
 *
 * Keyset statt Seitenzahlen, weil der Katalog rund 545.000 Titel hat und
 * OFFSET mit der Seitentiefe linear langsamer wird. Der Cursor ist
 * undurchsichtig und wird unveraendert zurueckgereicht.
 */
export async function fetchTitles(
  filters: TobisTitleFilters = EMPTY_TITLE_FILTERS,
  limit = 100,
  after?: string | null,
  sort: TobisTitleSort = "release",
  // Tonalitaets-Rangierung (Labor): rangiert die volle, durch die uebrigen
  // Filter eingegrenzte Kandidatenmenge nach Keyword-Ueberlappung, statt sie
  // auszuschliessen. Schliesst sich serverseitig mit `after` aus (siehe
  // routes/tobis.ts) - das Labor blaettert deshalb nicht, es zeigt eine Seite.
  keywordIds?: readonly string[],
  // Cast-/Crew-Rangierung (Regie/Drehbuchautor:in/Cast im Pitch) - dasselbe
  // Antwortform-Prinzip wie keywordIds, kann zusammen mit ihm gesetzt sein
  // (listTitlesRankedByTonality kombiniert beide Signale, Personen gehen vor).
  personIds?: readonly string[],
  // true = alle gewaehlten keywordIds muessen matchen ("und"), sonst reicht
  // einer ("oder", Default) - siehe TitleFilters.keywordMatchAll. Ohne
  // keywordIds wirkungslos, deshalb nur mitgeschickt, wenn beides gesetzt ist.
  keywordMatchAll?: boolean,
): Promise<TobisTitlePage> {
  const query = toQuery(
    {
      ...filters,
      sort,
      ...(keywordIds?.length ? { keywordIds } : {}),
      ...(personIds?.length ? { personIds } : {}),
      ...(keywordIds?.length && keywordMatchAll ? { keywordMatchAll } : {}),
    },
    limit,
  );
  const suffix = after ? `&after=${encodeURIComponent(after)}` : "";
  return getJson<TobisTitlePage>(`tobis-api/titles?${query}${suffix}`);
}

export interface KeywordMatch {
  keywordId: string;
  keyword: string;
  /** Anzahl Titel mit diesem Keyword im gesamten TMDB-Bestand. */
  titleCount: number;
}

/**
 * Uebersetzt Freitext-Kandidatenphrasen (aus dem Pitch-Text im Labor) in
 * echte keyword_ids aus dem TMDB-Bestand, fuer keywordIds oben. Reiner
 * DB-Abgleich (ODER-verknuepft, Substring, Rauschfilter im BFF) - keine
 * KI-Anfrage.
 */
export async function searchKeywords(
  terms: readonly string[],
  /** Obergrenze der Trefferliste, siehe searchKeywords im BFF. Ohne Angabe 50 -
   *  zu wenig fuer ein Vorschlagsfeld ("drama" hat 68 Treffer), passend fuer die
   *  KI-Extraktion, die viele Begriffe schickt und nur die staerksten braucht. */
  limit?: number,
): Promise<KeywordMatch[]> {
  if (!terms.length) return [];
  const query = new URLSearchParams({ terms: terms.join(",") });
  if (limit !== undefined) query.set("limit", String(limit));
  const { keywords } = await getJson<{ keywords: KeywordMatch[] }>(
    `tobis-api/keywords/search?${query.toString()}`,
  );
  return keywords;
}

export interface TobisTalentPage {
  talents: TobisTalent[];
  /** Gesamtzahl passend zu den Filtern - siehe TobisTitlePage.total. */
  total?: number;
}

export async function fetchTalents(
  filters: TobisTalentFilters = EMPTY_TALENT_FILTERS,
  limit = 100,
  sort: TobisTalentSort = "credits",
): Promise<TobisTalentPage> {
  // admissionsScope default im BFF ist "credited", das verlangt roleType
  // (sonst waere unklar, gegen welche Rolle summiert wird - siehe
  // TalentQuery im BFF). Ohne roleType-Filter faellt der Sort deshalb auf
  // "career" zurueck: gesamte Filmografie statt einer Rolle.
  const admissionsScope = sort === "admissions" ? (filters.roleType ? "credited" : "career") : undefined;
  const query = toQuery({ ...filters, sort, ...(admissionsScope ? { admissionsScope } : {}) }, limit);
  return getJson<TobisTalentPage>(`tobis-api/talents?${query}`);
}

/**
 * Nur Fotos, per Personen-ID statt Namenssuche (siehe TobisPersonPicker) -
 * ein einzelner indexierter Lookup (model.talent_unified), kein Credits-/
 * Skript-/TMDB-Aufwand wie bei fetchTalent (/tobis-api/talents/:personId).
 */
export async function fetchTalentPhotos(personIds: readonly string[]): Promise<Record<string, string | null>> {
  if (personIds.length === 0) return {};
  const { photos } = await getJson<{ photos: Record<string, string | null> }>(
    `tobis-api/talents/photos?ids=${encodeURIComponent(personIds.join(","))}`,
  );
  return photos;
}

export interface TobisCastMember {
  personId: string;
  personName: string | null;
  characterName: string | null;
  roleCount: number;
  isSelf: boolean;
  isVoice: boolean;
  /** Crew-Rollen wie "Director"/"Screenplay". Leer bei reinem Schauspiel und bei TVmaze. */
  jobs: string[];
  personPhotoUrl: string | null;
}

/** Eine Zeile der LUMIERE-Markttabelle. */
export interface TobisAdmissionMarket {
  /** ISO-Land, z.B. "DE". LUMIERE fuehrt zusaetzlich "GB_IE" als einen Markt. */
  market: string;
  admissions: string;
  distributor: string | null;
  releaseDate: string | null;
}

/**
 * Kinobesucher ueber alle Maerkte, die LUMIERE fuehrt.
 *
 * total ist ein Mindestwert und keine Weltauswertung: die Quelle nennt ihre
 * Zahlen selbst "minimum estimates" und fuehrt ausserhalb Europas nur, was ihr
 * gemeldet wurde. Jede Anzeige muss das mitsagen.
 */
export interface TobisAdmissions {
  total: string;
  /** Nur Maerkte mit Zahl. */
  marketCount: number;
  totalSinceYear: number | null;
  lumiereId: string;
  markets: TobisAdmissionMarket[];
}

/**
 * Die tragfaehigste Drehbuchfassung zu einem Titel (model.title_script,
 * V0035/V0037): eine Zeile je Film, die umfangreichste.
 */
export interface TobisTitleScript {
  /** 'imsdb' oder 'scriptslug'. */
  source: string;
  /** 'title+year', 'title+writer' oder 'title' - wie sicher die Zuordnung ist. */
  matchOrigin: string;
  words: number | null;
  scenes: number | null;
  nightScenes: number | null;
  exteriorScenes: number | null;
  locations: number | null;
  characters: number | null;
  scriptPath: string | null;
  pdfUrl: string | null;
}

/**
 * FFA-Foerderung, ueber Titel+Foerderjahr auf den Titel gematcht (siehe
 * apps/bff/db/migrations/V0044__FfaFoerderungHitlisteLegacy.sql). Das
 * Foerderjahr ist nicht das Kinostart-Jahr, deshalb nur eine Untermenge der
 * tatsaechlichen Foerderungen zu diesem Titel.
 */
export interface TobisFoerderung {
  bereich: string | null;
  jahr: number | null;
  kategorie: string | null;
  antragsteller: string | null;
  kinostart: string | null;
  foerdersummeEur: number | null;
}

/** Hitlisten-Treffer vor 2016 (Live-Connector deckt ab 2016), ergaenzt admissions. */
export interface TobisHitlisteLegacyRow {
  listPeriod: string;
  listYear: number;
  listScope: string | null;
  rank: number | null;
  distributor: string | null;
  admissionsPeriod: number | null;
  admissionsTotal: number | null;
  sourceUrl: string | null;
}

/** ISAN/EIDR/Wikidata-ID eines Titels (bulk.lumiere_film_identifier, V0067). */
export interface TobisTitleIdentifiers {
  isan: string | null;
  eidr: string | null;
  wikidataId: string | null;
}

/**
 * DFFF-Foerderprojektdaten (model.dfff_projekt, V0073), nur bei source='dfff'
 * gesetzt - deckt Felder ab, die das generische Title-Schema nicht hat (keine
 * model.credit-Zeilen fuer Cast/Crew, keine Foerdersumme). Getrennt von
 * TobisFoerderung (model.ffa_foerderung): andere Foerderstelle (BAFA statt
 * FFA), anderes Datenschema, keine Ueberlappung.
 */
export interface TobisDfffProjektInfo {
  produzent: string | null;
  koproduzenten: string | null;
  regie: string | null;
  autor: string | null;
  darsteller: string | null;
  verleih: string | null;
  drehbeginn: string | null;
  foerderungEur: number | null;
  zuschussEur: number | null;
  produktionsdienstleister: string | null;
  sourceUrl: string | null;
}

/**
 * Ein Messpunkt des Besucherverlaufs (model.title_admissions_series).
 *
 * Innerhalb einer Antwort ist periodType einheitlich - der BFF waehlt Monat
 * oder Jahr, nicht beides gemischt (siehe market.ts titleAdmissionsSeries).
 */
export interface TobisAdmissionsPoint {
  periodType: "month" | "year";
  /** ISO-Datum, bei Jahren der 1. Januar. */
  periodStart: string;
  /** Besucher in dieser Periode. */
  admissionsPeriod: number | null;
  /** Besucher kumuliert seit Kinostart. Die FFA fuehrt ihn nicht in jeder Liste. */
  admissionsTotal: number | null;
  bestRank: number | null;
}

export interface TobisTitleDetail {
  /** keywords/streamingOriginal nur hier: fuer 100 Zeilen einer Liste waere das unnoetiger Aufwand. */
  title: TobisTitle & {
    keywords: string[];
    dfff: TobisDfffProjektInfo | null;
    /** Heuristische Kennung, siehe db.ts TitleDetail.streamingOriginal. Keine belastbare Quelle. */
    streamingOriginal: boolean;
  };
  cast: TobisCastMember[];
  /** null, wenn der Titel keine LUMIERE-Zahlen hat (z.B. nur eine FFA-Zahl). */
  admissions: TobisAdmissions | null;
  /** null, wenn keine Quelle eine Drehbuchfassung zu diesem Titel zuordnet. */
  script: TobisTitleScript | null;
  foerderung: TobisFoerderung[];
  hitlisteLegacy: TobisHitlisteLegacyRow[];
  /** Besucherverlauf DE, leer wenn der Titel in keiner FFA-Hitliste steht. */
  admissionsSeries: TobisAdmissionsPoint[];
  /** null, wenn keines der drei Felder gesetzt ist. */
  identifiers: TobisTitleIdentifiers | null;
  /** Welt-Einspielergebnis (TMDB) je Abruf-Stand, chronologisch. Leer ohne TMDB-Match oder ohne gepflegten revenue-Wert. */
  revenueHistory: TobisRevenuePoint[];
}

export interface TobisRevenuePoint {
  /** Abrufdatum des TMDB-Stands als ISO-Datum. */
  fetchedOn: string;
  revenueUsd: number;
}

export async function fetchTitleDetail(
  tvmazeId: string,
  signal?: AbortSignal,
): Promise<TobisTitleDetail> {
  return getJson<TobisTitleDetail>(`tobis-api/titles/${encodeURIComponent(tvmazeId)}`, signal);
}

/**
 * Eine Quelle im Dossier: alles, was der Bestand zu einem Titel oder einer
 * Person hergibt, Relation fuer Relation.
 *
 * Immer Spalten plus Zeilen, auch bei einem einzelnen Satz - dadurch braucht
 * die Anzeige kein Wissen ueber die einzelnen Quellen. `label` ist der
 * technische Relationsname, weil der Zweck der Ansicht ist zu zeigen, WO etwas
 * liegt.
 */
export interface TobisDossierGroup {
  key: string;
  label: string;
  columns: string[];
  rows: (string | null)[][];
  /** Der Bezug ist geraten: ueber Titel+Jahr oder ueber den Klartextnamen. */
  caveat?: "titleYear" | "name" | "titleWriter";
  /** Die Abfrage ist gescheitert; rows ist dann leer. */
  failed?: true;
}

export async function fetchTitleDossier(tvmazeId: string): Promise<TobisDossierGroup[]> {
  const { groups } = await getJson<{ groups: TobisDossierGroup[] }>(
    `tobis-api/titles/${encodeURIComponent(tvmazeId)}/dossier`,
  );
  return groups;
}

export async function fetchTalentDossier(personId: string): Promise<TobisDossierGroup[]> {
  const { groups } = await getJson<{ groups: TobisDossierGroup[] }>(
    `tobis-api/talents/${encodeURIComponent(personId)}/dossier`,
  );
  return groups;
}

/** Eine Fassung, bei der diese Person als Drehbuchautor gefuehrt ist. */
export interface TobisTalentScript {
  title: string | null;
  year: number | null;
  imdbId: string;
  words: number | null;
  scenes: number | null;
}

/**
 * Kassen-Track-Record aus der Filmografie, siehe apps/bff/src/tobis/talent-score.ts.
 * totalAdmissionsDe ist bigint-sicher als String (grosse Filmografie kann
 * Number.MAX_SAFE_INTEGER ueberschreiten).
 */
export interface TobisBoxOfficeScore {
  totalAdmissionsDe: string;
  creditsWithAdmissions: number;
  totalCredits: number;
}

/**
 * Ein TMDB-Projekt dieser Person, das (noch) nicht in unserem Bestand steckt -
 * live von TMDBs /person/{id}/combined_credits nachgefragt (apps/bff/src/tobis/
 * tmdb-person-credits.ts), weil unsere eigene Discovery nur Titel mit
 * Erscheinungsdatum im abgesuchten Fenster findet. Kein model.credit-Eintrag,
 * deshalb kein tvmazeId zum Verlinken auf eine eigene Titelseite.
 */
export interface TmdbLiveCredit {
  tmdbId: string;
  titleName: string;
  year: number | null;
  characterName: string | null;
  jobs: string[];
  posterUrl: string | null;
}

export interface TobisFilmography {
  talent: TobisTalent;
  credits: TobisCredit[];
  /** TMDB-Projekte dieser Person, die unser Bestand (noch) nicht kennt. Leer, wenn TMDB nichts
   *  Zusaetzliches kennt, die Person keine TMDB-Person ist, oder TMDB_ACCESS_TOKEN fehlt. */
  liveCredits: TmdbLiveCredit[];
  /** Fassungen, bei denen diese Person als Drehbuchautor gefuehrt ist. Leer, wenn keine. */
  scripts: TobisTalentScript[];
  boxOffice: TobisBoxOfficeScore;
}

export async function fetchFilmography(personId: string): Promise<TobisFilmography> {
  return getJson<TobisFilmography>(`tobis-api/talents/${encodeURIComponent(personId)}`);
}

// Bewertungsschritt im Labor, siehe apps/bff/src/tobis/valuation.ts (dort auch
// die Fachbegruendung der Formel). Typen hier spiegeln die BFF-Antwort 1:1 -
// keine eigene Rechnung im Frontend, die Formel hat genau eine Heimat.

export type TobisAmountOrigin = "measured" | "derived" | "estimated" | "manual";

export interface TobisAmount {
  cents: number;
  origin: TobisAmountOrigin;
  source?: string;
}

export interface TobisValuation {
  modelVersion: string;
  revenueTotal: number;
  costTotal: number;
  subtotal: number;
  marginBase: number;
  margin: number;
  maxPurchasePrice: number;
  viable: boolean;
  manualPosts: string[];
  estimatedPosts: string[];
}

/** Ein Vergleichsfilm, wie er in die Kino-Erloes-Naeherung eingeht. */
export interface TobisValuationComparable {
  id: string;
  name?: string;
  admissionsDe: string | null;
  year: number | null;
}

/**
 * Die sechs Posten, fuer die der Bestand keine Quelle hat (siehe valuation.ts:
 * nur der Kino-Anteil laesst sich aus Vergleichsfilmen naehern). Euro-Cent.
 */
export interface TobisValuationManualInput {
  homeEntertainmentCents: number;
  payTvCents: number;
  freeTvCents: number;
  distributionSubsidyCents: number;
  paBudgetCents: number;
  internalCostCents: number;
}

export const EMPTY_VALUATION_MANUAL: TobisValuationManualInput = {
  homeEntertainmentCents: 0,
  payTvCents: 0,
  freeTvCents: 0,
  distributionSubsidyCents: 0,
  paBudgetCents: 0,
  internalCostCents: 0,
};

export interface TobisValuationEstimate {
  /** Die Quoten des Rechenmodells, nicht nur seine Version: die Rechenweg-Tooltips
   *  im Bewertungspanel nennen sie, und als Frontend-Konstante wuerden sie beim
   *  naechsten Modellwechsel still falsch. */
  model: {
    version: string;
    vatRate: number;
    ffaLevyRate: number;
    gemaGvlRate: number;
    distributorShareRate: number;
    marginRate: number;
  };
  revenue: {
    cinemaShare: TobisAmount;
    homeEntertainment: TobisAmount;
    payTv: TobisAmount;
    freeTv: TobisAmount;
    distributionSubsidy: TobisAmount;
  };
  cost: {
    paBudget: TobisAmount;
    internalCost: TobisAmount;
  };
  valuation: TobisValuation;
  /** Wie viele der gesendeten Vergleichsfilme tatsaechlich eine DE-Besucherzahl UND ein Jahr hatten. */
  comparablesUsed: number;
  /**
   * Eine volle Rechnung PRO Vergleichsfilm (siehe valuation.ts valuateByComparable) statt nur
   * des Schnitts oben - Grundlage fuer die Gauss-Verteilungskurve (TobisValuationDistribution.tsx).
   * Nur Vergleichsfilme MIT DE-Besucherzahl und Jahr sind hier vertreten (wie comparablesUsed).
   */
  perComparable: { id: string; name?: string; revenueTotal: number; maxPurchasePrice: number }[];
  /**
   * Die Zwischenschritte der Verleihmiete je Vergleichsfilm (valuation.ts
   * cinemaSharesByComparable) - Grundlage des Rechenwegs im Panel. Nur Filme MIT
   * DE-Besucherzahl und Jahr sind vertreten, wie bei perComparable.
   *
   * ticketPriceFromYear kann von year abweichen: fuer Jahrgaenge ausserhalb der
   * bekannten Preisreihe gilt der naechstgelegene, und das gehoert in den
   * Rechenweg.
   */
  cinemaShareSteps: {
    id: string;
    name?: string;
    admissions: number;
    year: number;
    ticketPriceCents: number;
    ticketPriceFromYear: number;
    grossCents: number;
    /** Nach MwSt bzw. nach FFA und GEMA/GVL - gerundete Darstellungswerte, siehe
     *  valuation.ts: die Rechnung selbst rundet erst am Ende. */
    netCents: number;
    afterLeviesCents: number;
    cinemaShareCents: number;
  }[];
}

/** Reine Rechnung, keine DB-Abfrage im BFF - der Pool liegt dem Frontend schon vor. */
export async function estimateValuation(
  comparables: readonly TobisValuationComparable[],
  manual: TobisValuationManualInput,
): Promise<TobisValuationEstimate> {
  const { response } = await apiRequest("tobis-api/valuation/estimate", {
    method: "POST",
    csrf: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comparables, manual }),
  });
  if (response.status === 501) throw new TobisNotConfiguredError();
  if (!response.ok) throw new Error(`tobis-api/valuation/estimate → ${response.status}`);
  return (await response.json()) as TobisValuationEstimate;
}

export type TriggerOutcome = "started" | "alreadyRunning" | "forbidden" | "failed";

// Der Lauf antwortet 202 und arbeitet im Hintergrund weiter: je Quelle dauert er
// wegen der Rate-Limits zweistellige Sekunden. Der Fortschritt kommt aus der
// Lauf-Liste, nicht aus dieser Antwort.
export async function triggerFetch(source: TobisSource, limit?: number): Promise<TriggerOutcome> {
  const { response } = await apiRequest(`tobis-api/fetch/${source}`, {
    method: "POST",
    csrf: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(limit ? { limit } : {}),
  });

  if (response.status === 202) return "started";
  if (response.status === 409) return "alreadyRunning";
  if (response.status === 403) return "forbidden";
  return "failed";
}

export function hasRunningRun(runs: TobisRun[]): boolean {
  return runs.some((r) => r.status === "running");
}

// Manueller Admin-Diagnosecheck (siehe apps/bff/src/tobis/boxoffice-check.ts):
// model.match_tconst matcht Boxoffice-/thenumbers-Zeilen ohne IMDb-ID per
// Titel+Jahr mit +/-1 Jahr Toleranz gegen den IMDb-Katalog - ein
// theoretisches Verwechslungsrisiko bei Remakes/Fortsetzungen mit demselben
// Titel in unterschiedlichen Jahren. domestic_gross > worldwide_gross ist der
// bewaehrte Gegencheck (Inland kann nie hoeher sein als Welt), aber bewusst
// kein automatisches Gate - Marvin loest ihn per Klick aus.
export interface TobisBoxOfficeMismatchRow {
  tconst: string;
  title: string | null;
  year: number | null;
  /** bigint-sicher als String, wie die uebrigen Geldbetraege im Bestand. */
  domesticGross: string;
  worldwideGross: string;
}

export interface TobisBoxOfficeMismatchCheck {
  total: number;
  anomalies: number;
  rows: TobisBoxOfficeMismatchRow[];
}

// Outcome-Objekt statt Exception, wie TriggerOutcome oben: 403/501 sind
// erwartete Antworten dieser Admin-Aktion (fehlende Rolle, keine konfigurierte
// DB), keine Ausnahmefaelle, die den Aufrufer zum try/catch zwingen sollten.
export type BoxOfficeMismatchOutcome =
  | { status: "ok"; check: TobisBoxOfficeMismatchCheck }
  | { status: "forbidden" }
  | { status: "notConfigured" }
  | { status: "failed" };

export async function runBoxOfficeMismatchCheck(): Promise<BoxOfficeMismatchOutcome> {
  const { response } = await apiRequest("tobis-api/checks/box-office-mismatch");

  if (response.status === 501) return { status: "notConfigured" };
  if (response.status === 403) return { status: "forbidden" };
  if (!response.ok) return { status: "failed" };

  return { status: "ok", check: (await response.json()) as TobisBoxOfficeMismatchCheck };
}

// Labor-Sitzungen (V0034 im BFF): die vorgelagerte Uebersicht "deine bisherigen
// Untersuchungen" vor dem Labor-Editor, im Zuschnitt von Match (MatchListDocument/
// CreateMatchDocument), aber ueber die REST-Schiene wie der Rest von Tobis.

export interface TobisLabSessionSummary {
  id: string;
  name: string | null;
  pitchTitle: string;
  pitchShort: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Denormalisierte Personen-Referenz aus dem Talent-Katalog (TobisTalent), fuer
 * die Pitch-Kriterien Regie/Drehbuchautor:in/Cast. Nur id+Name, kein voller
 * TobisTalent-Snapshot - derselbe Zuschnitt wie KeywordMatch fuer
 * keywordMatches: genug, um die Auswahl anzuzeigen und wieder aufzuloesen,
 * ohne den Talent-Datensatz zum Zeitpunkt der Auswahl einzufrieren.
 */
export interface TobisPersonRef {
  personId: string;
  personName: string | null;
}

/** Kriterien-Anteil einer Sitzung. Leer ({}) direkt nach createLabSession. */
export interface TobisLabSessionCriteria {
  filters: TobisTitleFilters;
  sort: TobisTitleSort;
  keywordMatches: KeywordMatch[];
  /** Regie. Kontext fuer die KI-Begruendung im Result-Schritt, kein Suchfilter. */
  director: TobisPersonRef | null;
  /** Drehbuchautor:in. Wie director: Kontext, kein Suchfilter. */
  writer: TobisPersonRef | null;
  /** Cast. Wie director: Kontext, kein Suchfilter. */
  cast: TobisPersonRef[];
  /** Komprimierte KI-Zusammenfassung eines hochgeladenen Skript-PDFs (TobisScriptSummaryController) -
   *  der Volltext wird nirgends persistiert, nur diese Zusammenfassung. null ohne Upload. */
  scriptSummary: string | null;
  /**
   * Vom Nutzer geschaetztes Budget/Einspielergebnis fuer den eigenen Pitch, USD wie
   * TobisTitle.budgetUsd/revenueUsd - optional (Marvin, 2026-08-07), kein Suchfilter: fliesst als
   * Kontext in findComparables ein und wird im Bewertungsschritt den Vergleichstiteln gegenuebergestellt.
   */
  expectedBudgetUsd: string | null;
  expectedRevenueUsd: string | null;
  /**
   * Ueberschreibt reasoning.comparable je Titel-Id im Bewertungsschritt
   * (TobisValuationPanel) - nur Ids, bei denen Tobi die KI-Vorauswahl von Hand
   * geaendert hat. Titel ohne Eintrag hier folgen weiter dem KI-Vorschlag.
   */
  compInclude: Record<string, boolean>;
  /** Manuelle Erloes-/Kostenposten des Bewertungsschritts, siehe TobisValuationManualInput. */
  valuationManual: TobisValuationManualInput;
  /**
   * Im Pitch-Text namentlich genannte, aufgeloeste Vergleichsfilme (siehe
   * resolveTitles/tobis-lab-resolve-titles.ts) - nur die Ids, volle
   * TobisTitle-Objekte werden beim Laden per fetchTitleDetail neu geholt.
   */
  pinnedTitleIds: string[];
  /**
   * "TOBIS Algorithmus"-Modus (Marvin, 2026-08-07) - siehe FindComparablesInput.tobisAlgorithm
   * (tobis-lab-find-comparables.ts). Default aus.
   */
  tobisAlgorithm: boolean;
}

// findComparables-Laeufe leben NICHT mehr in criteria (fruehere Fassung,
// 2026-08-07 morgens): ein Feld ueberschreiben hiesse, jeder neue Lauf
// verwirft den vorigen. Marvin brauchte stattdessen eine Uebersicht wie
// MatchResult - jeder Lauf ist jetzt eine eigene Zeile in
// app.lab_session_result (V0070), siehe fetchLabSessionResults/
// createLabSessionResult unten.

/** Eintrag in der "Fruehere Ergebnisse"-Liste - ohne `selected`/`context`, die
 *  Liste zeigt nur das Fazit, nicht die volle Begruendung je Titel. */
export interface TobisLabSessionResultSummary {
  id: string;
  createdAt: string;
  fazit: string | null;
  suitability: number | null;
  comparableCount: number;
}

export interface TobisLabSessionResultDetail extends TobisLabSessionResultSummary {
  selected: { id: string; reasoning: string }[];
  positive: string | null;
  negative: string | null;
  /** Der Prompt, den das Modell tatsaechlich gesehen hat, siehe find-comparables. */
  context: string | null;
}

export interface CreateLabSessionResultInput {
  selected: { id: string; reasoning: string }[];
  positive: string | null;
  negative: string | null;
  fazit: string | null;
  suitability: number | null;
  context: string | null;
}

export async function fetchLabSessionResults(sessionId: string): Promise<TobisLabSessionResultSummary[]> {
  return (
    await getJson<{ results: TobisLabSessionResultSummary[] }>(`tobis-api/lab-sessions/${sessionId}/results`)
  ).results;
}

export async function fetchLabSessionResult(
  sessionId: string,
  resultId: string,
): Promise<TobisLabSessionResultDetail> {
  return getJson<TobisLabSessionResultDetail>(`tobis-api/lab-sessions/${sessionId}/results/${resultId}`);
}

export async function createLabSessionResult(
  sessionId: string,
  input: CreateLabSessionResultInput,
): Promise<TobisLabSessionResultDetail> {
  const { response } = await apiRequest(`tobis-api/lab-sessions/${sessionId}/results`, {
    method: "POST",
    csrf: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`create lab-session-result → ${response.status}`);
  return (await response.json()) as TobisLabSessionResultDetail;
}

export async function deleteLabSessionResult(sessionId: string, resultId: string): Promise<void> {
  const { response } = await apiRequest(`tobis-api/lab-sessions/${sessionId}/results/${resultId}`, {
    method: "DELETE",
    csrf: true,
  });
  if (!response.ok) throw new Error(`delete lab-session-result → ${response.status}`);
}

export interface TobisLabSessionDetail extends TobisLabSessionSummary {
  pitchDescription: string;
  pitchHints: string;
  criteria: Partial<TobisLabSessionCriteria>;
}

export interface UpdateLabSessionInput {
  name?: string | null;
  pitchTitle?: string;
  pitchShort?: string;
  pitchDescription?: string;
  pitchHints?: string;
  criteria?: Partial<TobisLabSessionCriteria>;
}

export async function fetchLabSessions(): Promise<TobisLabSessionSummary[]> {
  return (await getJson<{ sessions: TobisLabSessionSummary[] }>("tobis-api/lab-sessions")).sessions;
}

// Geboren leer (Approach B wie bei Match, aber ohne Draft-Modal): fuers Labor
// gibt es kein Pflichtfeld vorab, der Klick auf "Neue Untersuchung" legt die
// Sitzung direkt an und navigiert in den Editor.
export async function createLabSession(): Promise<TobisLabSessionDetail> {
  // Leerer Body waere zusammen mit dem Content-Type-Header ein Fastify-Fehler
  // (FST_ERR_CTP_EMPTY_JSON_BODY, 400) - die Route selbst liest kein Feld
  // daraus, aber "{}" muss trotzdem mitgeschickt werden.
  const { response } = await apiRequest("tobis-api/lab-sessions", {
    method: "POST",
    csrf: true,
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  if (response.status === 501) throw new TobisNotConfiguredError();
  if (!response.ok) throw new Error(`tobis-api/lab-sessions → ${response.status}`);
  return (await response.json()) as TobisLabSessionDetail;
}

/** null bei 404 (geloescht/fremd) - ein legitimer Zustand fuer den Editor, kein Fehler. */
export async function fetchLabSession(id: string): Promise<TobisLabSessionDetail | null> {
  const { response } = await apiRequest(`tobis-api/lab-sessions/${encodeURIComponent(id)}`);
  if (response.status === 501) throw new TobisNotConfiguredError();
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`tobis-api/lab-sessions/${id} → ${response.status}`);
  return (await response.json()) as TobisLabSessionDetail;
}

/** Autosave-Ziel des Editors (debounced). Ein PATCH schickt meist nur die geaenderte Feldgruppe. */
export async function updateLabSession(
  id: string,
  patch: UpdateLabSessionInput,
): Promise<TobisLabSessionDetail | null> {
  const { response } = await apiRequest(`tobis-api/lab-sessions/${encodeURIComponent(id)}`, {
    method: "PATCH",
    csrf: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (response.status === 501) throw new TobisNotConfiguredError();
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`tobis-api/lab-sessions/${id} → ${response.status}`);
  return (await response.json()) as TobisLabSessionDetail;
}

export async function deleteLabSession(id: string): Promise<boolean> {
  const { response } = await apiRequest(`tobis-api/lab-sessions/${encodeURIComponent(id)}`, {
    method: "DELETE",
    csrf: true,
  });
  return response.status === 204;
}

// --- Startseite ----------------------------------------------------------

export interface TobisHomeCounts {
  today: number;
  week: number;
  latestAt: string | null;
}

export interface TobisHomeMarket {
  tvDay: string | null;
  tvDays: number;
  lastRunAt: string | null;
}

export interface TobisHomeTitle {
  id: string;
  source: string;
  name: string | null;
  medium: string | null;
  year: number | null;
  coverUrl: string | null;
  updatedAt: string | null;
}

export interface TobisHomeSummary {
  titles: TobisHomeCounts;
  talents: TobisHomeCounts;
  market: TobisHomeMarket;
  recentTitles: TobisHomeTitle[];
}

/**
 * Die Tobis-Zeile der Startseite. Ein Aufruf fuer alles, siehe
 * /tobis-api/home im BFF.
 *
 * Wirft TobisNotConfiguredError, wenn keine tobis-Datenbank haengt (501) - die
 * Startseite laesst die Zeile dann einfach weg, statt einen Fehler zu zeigen:
 * fuer jeden ausserhalb des Prototyps ist das kein Fehler, sondern Normalzustand.
 */
export async function fetchTobisHome(
  limit = 12,
  signal?: AbortSignal,
): Promise<TobisHomeSummary> {
  return getJson<TobisHomeSummary>(`tobis-api/home?limit=${limit}`, signal);
}
